import { lazy, Suspense } from 'react'
import { Navigate, Outlet, Route, Routes } from 'react-router-dom'
import { RequireAdminAuth } from './admin/RequireAdminAuth'
import { RequireAuth } from './auth/RequireAuth'
import { RequirePackage2 } from './auth/RequirePackage2'
import { RequirePackage3 } from './auth/RequirePackage3'
import { RequirePackage4 } from './auth/RequirePackage4'
import { AdminShell } from './components/AdminShell'
import { AppShell } from './components/AppShell'
import { PublicLayout } from './components/PublicLayout'
import { NavLayoutProvider } from './layout/NavLayoutContext'
import { ThemeProvider } from './theme/ThemeContext'

const AdminAiRolesPage = lazy(() =>
  import('./pages/admin/AdminAiRolesPage').then((module) => ({
    default: module.AdminAiRolesPage,
  })),
)
const AdminAiUsagePage = lazy(() =>
  import('./pages/admin/AdminAiUsagePage').then((module) => ({
    default: module.AdminAiUsagePage,
  })),
)
const AdminAiPricingPage = lazy(() =>
  import('./pages/admin/AdminAiPricingPage').then((module) => ({
    default: module.AdminAiPricingPage,
  })),
)
const AdminAnalyticsPage = lazy(() =>
  import('./pages/admin/AdminAnalyticsPage').then((module) => ({
    default: module.AdminAnalyticsPage,
  })),
)
const AdminDashboardPage = lazy(() =>
  import('./pages/admin/AdminDashboardPage').then((module) => ({
    default: module.AdminDashboardPage,
  })),
)
const AdminLoginPage = lazy(() =>
  import('./pages/admin/AdminLoginPage').then((module) => ({
    default: module.AdminLoginPage,
  })),
)
const AdminOperationsPage = lazy(() =>
  import('./pages/admin/AdminOperationsPage').then((module) => ({
    default: module.AdminOperationsPage,
  })),
)
const AdminPackagesPage = lazy(() =>
  import('./pages/admin/AdminPackagesPage').then((module) => ({
    default: module.AdminPackagesPage,
  })),
)
const AdminSettingsPage = lazy(() =>
  import('./pages/admin/AdminSettingsPage').then((module) => ({
    default: module.AdminSettingsPage,
  })),
)
const AdminTemplatesPage = lazy(() =>
  import('./pages/admin/AdminTemplatesPage').then((module) => ({
    default: module.AdminTemplatesPage,
  })),
)
const AdminUsersPage = lazy(() =>
  import('./pages/admin/AdminUsersPage').then((module) => ({
    default: module.AdminUsersPage,
  })),
)
const BillingPage = lazy(() =>
  import('./pages/users/BillingPage').then((module) => ({
    default: module.BillingPage,
  })),
)
const CreateCustomerPage = lazy(() =>
  import('./pages/users/CreateCustomerPage').then((module) => ({
    default: module.CreateCustomerPage,
  })),
)
const CreateOfferPage = lazy(() =>
  import('./pages/users/CreateOfferPage').then((module) => ({
    default: module.CreateOfferPage,
  })),
)
const OfferCapturePage = lazy(() =>
  import('./pages/users/OfferCapturePage').then((module) => ({
    default: module.OfferCapturePage,
  })),
)
const CustomerDetailPage = lazy(() =>
  import('./pages/users/CustomerDetailPage').then((module) => ({
    default: module.CustomerDetailPage,
  })),
)
const CustomerTasksPage = lazy(() =>
  import('./pages/users/CustomerTasksPage').then((module) => ({
    default: module.CustomerTasksPage,
  })),
)
const CreateCustomerTaskPage = lazy(() =>
  import('./pages/users/CreateCustomerTaskPage').then((module) => ({
    default: module.CreateCustomerTaskPage,
  })),
)
const AppointmentsCalendarPage = lazy(() =>
  import('./pages/users/AppointmentsCalendarPage').then((module) => ({
    default: module.AppointmentsCalendarPage,
  })),
)
const CreateAppointmentPage = lazy(() =>
  import('./pages/users/CreateAppointmentPage').then((module) => ({
    default: module.CreateAppointmentPage,
  })),
)
const EditAppointmentPage = lazy(() =>
  import('./pages/users/EditAppointmentPage').then((module) => ({
    default: module.EditAppointmentPage,
  })),
)
const CustomersList = lazy(() =>
  import('./pages/users/CustomersList').then((module) => ({
    default: module.CustomersList,
  })),
)
const DashboardPage = lazy(() =>
  import('./pages/users/DashboardPage').then((module) => ({
    default: module.DashboardPage,
  })),
)
const ExpenseCapturePage = lazy(() =>
  import('./pages/users/ExpenseCapturePage').then((module) => ({
    default: module.ExpenseCapturePage,
  })),
)
const ExpensesPage = lazy(() =>
  import('./pages/users/ExpensesPage').then((module) => ({
    default: module.ExpensesPage,
  })),
)
const FollowUpOptOutPage = lazy(() =>
  import('./pages/users/FollowUpOptOutPage').then((module) => ({
    default: module.FollowUpOptOutPage,
  })),
)
const LoginPage = lazy(() =>
  import('./pages/users/LoginPage').then((module) => ({
    default: module.LoginPage,
  })),
)
const NotificationsPage = lazy(() =>
  import('./pages/users/NotificationsPage').then((module) => ({
    default: module.NotificationsPage,
  })),
)
const OfferAcceptPage = lazy(() =>
  import('./pages/users/OfferAcceptPage').then((module) => ({
    default: module.OfferAcceptPage,
  })),
)
const OfferDetail = lazy(() =>
  import('./pages/users/OfferDetail').then((module) => ({
    default: module.OfferDetail,
  })),
)
const PackageDetailsPage = lazy(() =>
  import('./pages/users/PackageDetailsPage').then((module) => ({
    default: module.PackageDetailsPage,
  })),
)
const OffersList = lazy(() =>
  import('./pages/users/OffersList').then((module) => ({
    default: module.OffersList,
  })),
)
const ProjectsPage = lazy(() =>
  import('./pages/users/ProjectsPage').then((module) => ({
    default: module.ProjectsPage,
  })),
)
const CreateProjectPage = lazy(() =>
  import('./pages/users/CreateProjectPage').then((module) => ({
    default: module.CreateProjectPage,
  })),
)
const EditProjectPage = lazy(() =>
  import('./pages/users/EditProjectPage').then((module) => ({
    default: module.EditProjectPage,
  })),
)
const PublicProfilePage = lazy(() =>
  import('./pages/users/PublicProfilePage').then((module) => ({
    default: module.PublicProfilePage,
  })),
)
const PublicProfileProjectPage = lazy(() =>
  import('./pages/users/PublicProfileProjectPage').then((module) => ({
    default: module.PublicProfileProjectPage,
  })),
)
const RegisterPage = lazy(() =>
  import('./pages/users/RegisterPage').then((module) => ({
    default: module.RegisterPage,
  })),
)
const VerifyEmailPage = lazy(() =>
  import('./pages/users/VerifyEmailPage').then((module) => ({
    default: module.VerifyEmailPage,
  })),
)
const VerifyEmailPendingPage = lazy(() =>
  import('./pages/users/VerifyEmailPendingPage').then((module) => ({
    default: module.VerifyEmailPendingPage,
  })),
)
const ForgotPasswordPage = lazy(() =>
  import('./pages/users/ForgotPasswordPage').then((module) => ({
    default: module.ForgotPasswordPage,
  })),
)
const ResetPasswordPage = lazy(() =>
  import('./pages/users/ResetPasswordPage').then((module) => ({
    default: module.ResetPasswordPage,
  })),
)
const ReportsLayout = lazy(() =>
  import('./pages/users/reports/ReportsLayout').then((module) => ({
    default: module.ReportsLayout,
  })),
)
const ReportsStatisticsPage = lazy(() =>
  import('./pages/users/reports/ReportsStatisticsPage').then((module) => ({
    default: module.ReportsStatisticsPage,
  })),
)
const ReportsOffersPage = lazy(() =>
  import('./pages/users/reports/ReportsOffersPage').then((module) => ({
    default: module.ReportsOffersPage,
  })),
)
const ReportsCrmPage = lazy(() =>
  import('./pages/users/reports/ReportsCrmPage').then((module) => ({
    default: module.ReportsCrmPage,
  })),
)
const ReportsOperationsPage = lazy(() =>
  import('./pages/users/reports/ReportsOperationsPage').then((module) => ({
    default: module.ReportsOperationsPage,
  })),
)
const ReportsFinancialsPage = lazy(() =>
  import('./pages/users/reports/ReportsFinancialsPage').then((module) => ({
    default: module.ReportsFinancialsPage,
  })),
)
const ReportsProfilePage = lazy(() =>
  import('./pages/users/reports/ReportsProfilePage').then((module) => ({
    default: module.ReportsProfilePage,
  })),
)
const ReportsComparePage = lazy(() =>
  import('./pages/users/reports/ReportsComparePage').then((module) => ({
    default: module.ReportsComparePage,
  })),
)
const ReportsWeeklyPage = lazy(() =>
  import('./pages/users/reports/ReportsWeeklyPage').then((module) => ({
    default: module.ReportsWeeklyPage,
  })),
)
const ReportsInsightsPage = lazy(() =>
  import('./pages/users/reports/ReportsInsightsPage').then((module) => ({
    default: module.ReportsInsightsPage,
  })),
)
const ReviewsManagePage = lazy(() =>
  import('./pages/users/ReviewsManagePage').then((module) => ({
    default: module.ReviewsManagePage,
  })),
)
const ReviewSubmitPage = lazy(() =>
  import('./pages/users/ReviewSubmitPage').then((module) => ({
    default: module.ReviewSubmitPage,
  })),
)
const SettingsPage = lazy(() =>
  import('./pages/users/SettingsPage').then((module) => ({
    default: module.SettingsPage,
  })),
)
const LegalDocumentPage = lazy(() =>
  import('./pages/public/LegalDocumentPage').then((module) => ({
    default: module.LegalDocumentPage,
  })),
)
const OnboardingPage = lazy(() =>
  import('./pages/users/OnboardingPage').then((module) => ({
    default: module.OnboardingPage,
  })),
)

function RouteFallback() {
  return (
    <div className="mx-auto max-w-lg px-4 py-16 text-center text-sm text-zinc-600 dark:text-zinc-400">
      Φόρτωση σελίδας…
    </div>
  )
}

function App() {
  return (
    <ThemeProvider>
      <NavLayoutProvider>
      <Suspense fallback={<RouteFallback />}>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/verify-email-pending" element={<VerifyEmailPendingPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route element={<PublicLayout />}>
            <Route path="/packages" element={<Navigate to="/packages/basic" replace />} />
            <Route path="/packages/:packageSlug" element={<PackageDetailsPage />} />
            <Route
              path="/u/:slug/project/:projectId"
              element={<PublicProfileProjectPage />}
            />
            <Route path="/u/:slug" element={<PublicProfilePage />} />
            <Route path="/review" element={<ReviewSubmitPage />} />
            <Route path="/offer-accept" element={<OfferAcceptPage />} />
            <Route path="/follow-up-opt-out" element={<FollowUpOptOutPage />} />
            <Route path="/terms" element={<LegalDocumentPage />} />
            <Route path="/privacy" element={<LegalDocumentPage />} />
          </Route>
          {/*
            Nested κάτω από /admin ώστε το /admin/login να μην «τρώγεται» από το path="*"
            κάτω από RequireAuth (React Router 7 — αλλιώς κενή οθόνη / λάθος redirect).
          */}
          <Route path="/admin" element={<Outlet />}>
            <Route path="login" element={<AdminLoginPage />} />
            <Route element={<RequireAdminAuth />}>
              <Route element={<AdminShell />}>
                <Route index element={<AdminDashboardPage />} />
                <Route path="users" element={<AdminUsersPage />} />
                <Route path="packages" element={<AdminPackagesPage />} />
                <Route path="analytics" element={<AdminAnalyticsPage />} />
                <Route path="ai-usage" element={<AdminAiUsagePage />} />
                <Route path="ai-pricing" element={<AdminAiPricingPage />} />
                <Route path="operations" element={<AdminOperationsPage />} />
                <Route path="ai-roles" element={<AdminAiRolesPage />} />
                <Route path="templates" element={<AdminTemplatesPage />} />
                <Route path="settings" element={<AdminSettingsPage />} />
              </Route>
            </Route>
          </Route>
          <Route element={<RequireAuth />}>
            <Route element={<AppShell />}>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/offers" element={<OffersList />} />
              <Route path="/customers" element={<CustomersList />} />
              <Route element={<RequirePackage2 />}>
                <Route path="/projects/new" element={<CreateProjectPage />} />
                <Route path="/projects/:projectId/edit" element={<EditProjectPage />} />
                <Route path="/projects" element={<ProjectsPage />} />
                <Route path="/reviews" element={<ReviewsManagePage />} />
              </Route>
              <Route element={<RequirePackage3 />}>
                <Route path="/expenses/new" element={<ExpenseCapturePage />} />
                <Route path="/expenses" element={<ExpensesPage />} />
                <Route path="/notifications" element={<NotificationsPage />} />
                <Route path="/appointments/new" element={<CreateAppointmentPage />} />
                <Route path="/appointments/:id/edit" element={<EditAppointmentPage />} />
                <Route path="/appointments" element={<AppointmentsCalendarPage />} />
              </Route>
              <Route element={<RequirePackage4 />}>
                <Route path="/reports" element={<ReportsLayout />}>
                  <Route index element={<ReportsStatisticsPage />} />
                  <Route path="statistics" element={<Navigate to="/reports" replace />} />
                  <Route path="offers" element={<ReportsOffersPage />} />
                  <Route path="crm" element={<ReportsCrmPage />} />
                  <Route path="operations" element={<ReportsOperationsPage />} />
                  <Route path="financials" element={<ReportsFinancialsPage />} />
                  <Route path="profile" element={<ReportsProfilePage />} />
                  <Route path="compare" element={<ReportsComparePage />} />
                  <Route path="weekly" element={<ReportsWeeklyPage />} />
                  <Route path="insights" element={<ReportsInsightsPage />} />
                </Route>
              </Route>
              <Route path="/customers/new" element={<CreateCustomerPage />} />
              <Route path="/customers/tasks/new" element={<CreateCustomerTaskPage />} />
              <Route path="/customers/tasks" element={<CustomerTasksPage />} />
              <Route path="/customers/:id" element={<CustomerDetailPage />} />
              <Route path="/offers/capture" element={<OfferCapturePage />} />
              <Route path="/offers/new" element={<CreateOfferPage />} />
              <Route path="/offers/:id" element={<OfferDetail />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/billing" element={<BillingPage />} />
              <Route path="/onboarding" element={<OnboardingPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      </NavLayoutProvider>
    </ThemeProvider>
  )
}

export default App
